import {useLocation, useParams} from '@github-ui/react-router'
import {useNavigate} from '@github-ui/use-navigate'
import {useCallback, useMemo} from 'react'

import {issuesIndexQueryResolver} from '../helpers/issues-index-query-resolver'

/**
 * Repo Issues#index query/navigation state: resolves the query from the URL and
 * navigates `/:owner/:repo/issues`. Shared by the search input and results list.
 */
export function useIssuesIndexResults() {
  const location = useLocation()
  const {owner, repo} = useParams<{owner: string; repo: string}>()

  return useIssuesIndexResultsForRoute({
    pathname: location.pathname,
    search: location.search,
    owner,
    repo,
  })
}

export function useIssuesIndexResultsForRoute({
  pathname: currentPathname,
  search,
  owner,
  repo,
}: {
  pathname: string
  search: string
  owner?: string
  repo?: string
}) {
  const navigate = useNavigate()
  const searchParams = useMemo(() => new URLSearchParams(search), [search])
  const fetchedQuery = useMemo(
    () => issuesIndexQueryResolver({pathname: currentPathname, searchParams}),
    [currentPathname, searchParams],
  )
  const currentPage = Number(searchParams.get('page') || 1)
  const pathname = owner && repo ? `/${owner}/${repo}/issues` : currentPathname

  const buildSearch = useCallback(
    (q: string, page = 1) => {
      const nextSearchParams = new URLSearchParams()
      const normalizedQuery = q.trim()
      if (normalizedQuery) nextSearchParams.set('q', normalizedQuery)
      if (page > 1) nextSearchParams.set('page', String(page))

      for (const [key, value] of searchParams) {
        if (key !== 'q' && key !== 'page') nextSearchParams.append(key, value)
      }

      return nextSearchParams.toString()
    },
    [searchParams],
  )

  const getQueryHref = useCallback(
    (q: string, page = 1) => {
      const nextSearch = buildSearch(q, page)
      return `${pathname}${nextSearch ? `?${nextSearch}` : ''}`
    },
    [buildSearch, pathname],
  )

  const fetchResults = useCallback(
    ({q, page = 1}: {q: string; page?: number}) => {
      const nextSearch = buildSearch(q, page)
      navigate({pathname, search: nextSearch ? `?${nextSearch}` : ''})
      window.scrollTo({top: 0})
    },
    [buildSearch, navigate, pathname],
  )

  const getPageHref = useCallback((page: number) => getQueryHref(fetchedQuery, page), [fetchedQuery, getQueryHref])

  return useMemo(
    () => ({currentPage, fetchedQuery, fetchResults, getPageHref, getQueryHref}),
    [currentPage, fetchResults, fetchedQuery, getPageHref, getQueryHref],
  )
}
